import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  AlertCircle,
  Bookmark,
  Eye,
  ArrowRight,
  Upload,
  Camera,
  Briefcase,
  ShieldAlert,
  GraduationCap,
  FileText,
  Globe,
  MapPin,
  Phone,
  Wrench,
  Clock,
  Target,
  User,
  Heart,
} from "lucide-react";
import sh from "bundles/common/utils/sh";
import { getAppPipeline } from "./applicationPipeline";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/nl";
import ProfilePicture from "bundles/common/components/ProfilePicture";
import NewJobOfferCard from "bundles/Candidate/components/NewJobOfferCard";
import CompanyCard from "bundles/Candidate/components/CompanyCard";
import EmptyState from "./EmptyState";

const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

const getStrengthColor = (pct) =>
  pct < 40 ? "hsl(0, 70%, 55%)" : pct < 70 ? "hsl(45, 90%, 50%)" : "hsl(153 68% 25%)";

const ProfileGauge = ({ percent }) => {
  const intl = useIntl();
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  const color = getStrengthColor(percent);
  return (
    <svg width="92" height="92" viewBox="0 0 92 92" style={{ flexShrink: 0 }}>
      <circle cx="46" cy="46" r={r} fill="none" stroke="hsl(160, 10%, 96%)" strokeWidth="5" />
      <motion.circle
        cx="46" cy="46" r={r} fill="none"
        stroke={color} strokeWidth="5"
        strokeDasharray={circ}
        strokeLinecap="round"
        transform="rotate(-90 46 46)"
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
      <text x="46" y="42" textAnchor="middle" dominantBaseline="central" fill="hsl(178, 93%, 10%)" style={{ fontSize: "18px", fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
        {percent}%
      </text>
      <text x="46" y="57" textAnchor="middle" dominantBaseline="central" fill="hsl(178, 10%, 45%)" style={{ fontSize: "8px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
        {intl.formatMessage({ id: "candidate.dashboard.gauge_completed" })}
      </text>
    </svg>
  );
};

const attributeTipMap = {
  resume_url: { textId: "candidate.dashboard.tips.resume_url", weight: 30, icon: Upload, section: "documents" },
  profile_picture_url: { textId: "candidate.dashboard.tips.profile_picture_url", weight: 10, icon: Camera, section: "basic" },
  experiences_count: { textId: "candidate.dashboard.tips.experiences_count", weight: 10, icon: Briefcase, section: "experience" },
  trainings_count: { textId: "candidate.dashboard.tips.trainings_count", weight: 10, icon: GraduationCap, section: "education" },
  resource_skills_count: { textId: "candidate.dashboard.tips.resource_skills_count", weight: 8, icon: Wrench, section: "skills" },
  presentation: { textId: "candidate.dashboard.tips.presentation", weight: 4, icon: FileText, section: "headline" },
  desired_sector: { textId: "candidate.dashboard.tips.desired_sector", weight: 4, icon: Target, section: "basic" },
  availability: { textId: "candidate.dashboard.tips.availability", weight: 4, icon: Clock, section: "preferences" },
  region: { textId: "candidate.dashboard.tips.region", weight: 2, icon: MapPin, section: "basic" },
  phone: { textId: "candidate.dashboard.tips.phone", weight: 2, icon: Phone, section: "basic" },
  portfolio_url: { textId: "candidate.dashboard.tips.portfolio_url", weight: 2, icon: Globe, section: "documents" },
  job_title: { textId: "candidate.dashboard.tips.job_title", weight: 2, icon: User, section: "headline" },
};

const DashboardTab = ({ onNavigate, active = true }) => {
  const intl = useIntl();
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  moment.locale(i18nLocale === "fr-fr" ? "fr" : (i18nLocale || "fr"));

  const notifyActionFailed = useCallback(() => {
    enqueueSnackbar(
      intl.formatMessage({ id: "candidate.dashboard.action_failed", defaultMessage: "Une erreur est survenue. Réessayez." }),
      { variant: "error" }
    );
  }, [enqueueSnackbar, intl]);

  const handleUnfavorite = useCallback((e, jobId) => {
    e.stopPropagation();
    const updatedFavorites = (currentCandidate?.favorite_offers || []).filter(
      (id) => String(id) !== String(jobId)
    );
    sh.put("update_favorite_offers", {
      authenticity_token: authenticityToken,
      favorite_offers: updatedFavorites,
    }).then((res) => {
      if (res.data) {
        dispatch({ type: "UPDATE_CURRENT_CANDIDATE", payload: res.data });
        setData((prev) => prev ? {
          ...prev,
          saved_jobs: prev.saved_jobs.filter((j) => String(j.id) !== String(jobId)),
        } : prev);
      }
    }).catch(notifyActionFailed);
  }, [currentCandidate, authenticityToken, dispatch, notifyActionFailed]);

  const [dismissedTips] = useState([]);
  const [tappedStage, setTappedStage] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const completionPercent = data?.profile?.completion_percent ?? currentCandidate?.profile_completion ?? 0;
  const missingAttributes = data?.profile?.missing_attributes ?? currentCandidate?.missing_attributes ?? [];
  const profileViewsCount = data?.profile?.profile_views_count ?? currentCandidate?.candidate_interests_count ?? 0;
  const profileViewers = data?.profile?.profile_viewers ?? [];
  const recommendedJobs = data?.recommended_jobs ?? [];
  const savedJobs = data?.saved_jobs ?? [];
  const applications = data?.applications ?? [];
  const matchedCompanies = data?.matched_companies ?? [];
  const followedCompanies = data?.followed_companies ?? [];

  const handleFollowToggle = useCallback((companyId) => {
    if (!currentCandidate?.id) return;

    const currentIds = (currentCandidate?.followed_company_ids || []).map(String);
    const cid = String(companyId);
    const isCurrentlyFollowed = currentIds.includes(cid);
    const updatedIds = isCurrentlyFollowed
      ? currentIds.filter((id) => id !== cid)
      : [...currentIds, cid];

    sh.put("update_followed_companies", {
      authenticity_token: authenticityToken,
      followed_company_ids: updatedIds,
    }).then((res) => {
      if (res.data) {
        dispatch({ type: "UPDATE_CURRENT_CANDIDATE", payload: res.data });
        setData((prev) => {
          if (!prev) return prev;
          if (isCurrentlyFollowed) {
            return {
              ...prev,
              followed_companies: prev.followed_companies.filter((c) => String(c.id) !== cid),
            };
          }
          const companyData = prev.matched_companies?.find((c) => String(c.id) === cid);
          if (companyData) {
            return {
              ...prev,
              followed_companies: [...prev.followed_companies, {
                id: companyData.id,
                name: companyData.name,
                city: companyData.city,
                region: companyData.region,
                company_industry: companyData.company_industry,
                encoded_name: companyData.encoded_name,
                logo_url: companyData.logo_url,
                small_logo_url: companyData.small_logo_url,
                online_job_offers_count: companyData.online_job_offers_count || 0,
              }],
            };
          }
          return prev;
        });
      }
    }).catch(notifyActionFailed);
  }, [currentCandidate, authenticityToken, dispatch, notifyActionFailed]);

  const handleUnfollow = useCallback((e, companyId) => {
    e.stopPropagation();
    const updatedIds = (currentCandidate?.followed_company_ids || [])
      .map(String)
      .filter((id) => id !== String(companyId));
    sh.put("update_followed_companies", {
      authenticity_token: authenticityToken,
      followed_company_ids: updatedIds,
    }).then((res) => {
      if (res.data) {
        dispatch({ type: "UPDATE_CURRENT_CANDIDATE", payload: res.data });
        setData((prev) => prev ? {
          ...prev,
          followed_companies: prev.followed_companies.filter((c) => String(c.id) !== String(companyId)),
        } : prev);
      }
    }).catch(notifyActionFailed);
  }, [currentCandidate, authenticityToken, dispatch, notifyActionFailed]);

  const visibleTips = missingAttributes
    .filter((attr) => {
      const key = typeof attr === "string" ? attr : attr.attribute_name;
      return attributeTipMap[key] && !dismissedTips.includes(key);
    })
    .map((attr) => {
      const key = typeof attr === "string" ? attr : attr.attribute_name;
      return { id: key, ...attributeTipMap[key] };
    })
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 3);

  useEffect(() => {
    // Tabs stay mounted in the dashboard shell, so refetch whenever this tab
    // becomes active again (e.g. after applying to a job) to avoid stale data.
    if (!active) return undefined;
    let cancelled = false;
    const fetchDashboard = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const res = await sh.get("/candidate/dashboard_data");
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDashboard();
    return () => { cancelled = true; };
  }, [active, reloadKey, currentCandidate?.id, currentCandidate?.resume_url, currentCandidate?.profile_completion]);

  const pipelineApps = applications.slice(0, 2);

  // Nothing to show yet: the branded loader alone, centred in the tab, rather than a greeting
  // followed by a loader pinned to the bottom of the content.
  if (loading && !data) return <SquarehubLoader size={56} centered minHeight="60vh" />;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* Greeting */}
        <motion.div variants={item}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontFamily: "Segoe UI Black, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", letterSpacing: "-0.025em" }}>
                {intl.formatMessage({ id: "candidate.dashboard.greeting" }, { name: currentCandidate?.first_name || "" })}
              </Typography>
              <Typography sx={{ color: "hsl(178, 10%, 45%)", fontSize: "14px", mt: "2px", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                {intl.formatMessage({ id: "candidate.dashboard.greeting_subtitle" })}
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Mobile: completion warning surfaced at the top (the right column
            with the gauge stacks below everything on phones) */}
        {completionPercent < 60 && !loading && (
          <motion.div variants={item}>
            <Box
              component="button"
              onClick={() => onNavigate("profile")}
              sx={{
                display: { xs: "flex", lg: "none" }, width: "100%",
                alignItems: "flex-start", gap: "10px", textAlign: "left",
                borderRadius: "12px", bgcolor: "hsla(0, 84.2%, 60.2%, 0.05)",
                border: "1px solid hsla(0, 84.2%, 60.2%, 0.15)",
                px: "14px", py: "12px", cursor: "pointer",
              }}
            >
              <ShieldAlert size={16} color="hsl(0, 84.2%, 60.2%)" style={{ flexShrink: 0, marginTop: "1px" }} />
              <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsla(0, 84.2%, 60.2%, 0.9)", lineHeight: 1.5 }}>
                {intl.formatMessage({ id: "candidate.dashboard.visibility_warning_prefix" })}
                <Typography component="span" sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px", color: "hsl(0, 84.2%, 60.2%)" }}>60%</Typography>
                {intl.formatMessage({ id: "candidate.dashboard.visibility_warning_suffix" })}
              </Typography>
              <ArrowRight size={14} color="hsl(0, 84.2%, 60.2%)" style={{ flexShrink: 0, marginTop: "1px" }} />
            </Box>
          </motion.div>
        )}

        {/* Fetch error: explicit state with retry instead of fake empty sections */}
        {fetchError && !data && !loading && (
          <EmptyState
            icon={AlertCircle}
            py="64px"
            card
            title={intl.formatMessage({ id: "candidate.dashboard.load_error_title", defaultMessage: "Impossible de charger les données" })}
            subtitle={intl.formatMessage({ id: "candidate.dashboard.load_error_hint", defaultMessage: "Vérifiez votre connexion internet." })}
            ctaLabel={intl.formatMessage({ id: "candidate.dashboard.retry", defaultMessage: "Réessayer" })}
            onCta={() => setReloadKey((k) => k + 1)}
          />
        )}

        {/* Main grid */}
        {!(loading && !data) && !(fetchError && !data) && (
          <motion.div variants={item}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "8fr 4fr" }, gap: "24px" }}>

              {/* Left column */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* Recommended jobs */}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.recommended_jobs" })}
                    </Typography>
                    {/* <Box
                    component="button"
                    onClick={() => onNavigate("jobs")}
                    sx={{
                      fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", display: "flex", alignItems: "center", gap: "4px",
                      border: "none", bgcolor: "transparent", cursor: "pointer", "&:hover": { opacity: 0.8 },
                    }}
                  >
                    Tout voir <ArrowRight size={12} />
                  </Box> */}
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
                    {recommendedJobs.slice(0, 4).map((job) => (
                      <motion.div key={job.id} variants={item}>
                        <NewJobOfferCard
                          offer={job}
                          onClick={() => onNavigate("jobs", job)}
                          hideDescription
                          applied={Boolean(currentCandidate?.id && job.job_applications?.some(
                            (a) => String(a.candidate_id) === String(currentCandidate.id)
                          ))}
                        />
                      </motion.div>
                    ))}
                    {recommendedJobs.length === 0 && !loading && (
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <EmptyState
                          icon={Briefcase}
                          title={intl.formatMessage({ id: "candidate.dashboard.no_recommended_jobs" })}
                          subtitle={intl.formatMessage({ id: "candidate.dashboard.no_recommended_jobs_hint" })}
                          ctaLabel={intl.formatMessage({ id: "candidate.dashboard.complete_my_profile" })}
                          onCta={() => onNavigate("profile")}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Active applications */}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.active_applications" })}
                    </Typography>
                    <Box
                      component="button"
                      onClick={() => onNavigate("applications")}
                      sx={{
                        fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", display: "flex", alignItems: "center", gap: "4px",
                        border: "none", bgcolor: "transparent", cursor: "pointer", transition: "opacity 0.15s",
                        "&:hover": { opacity: 0.8 },
                      }}
                    >
                      {intl.formatMessage({ id: "candidate.dashboard.see_all" })} <ArrowRight size={12} />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {pipelineApps.map((app) => {
                      const { stages, current } = getAppPipeline(app);
                      const company = app.company || app.job_offer?.company || {};
                      return (
                        <motion.div key={app.id} variants={item}>
                          <Box
                            onClick={() => onNavigate("applications")}
                            sx={{
                              bgcolor: "hsl(0, 0%, 100%)",
                              borderRadius: "16px",
                              border: "1px solid hsla(160, 10%, 90%, 0.6)",
                              p: "20px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: "hsla(145, 63%, 42%, 0.3)",
                                boxShadow: "0 5px 5px -3px hsla(145, 63%, 42%, 0.05),0 4px 6px -4px hsla(145, 63%, 42%, 0.05)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "16px" }}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                                <ProfilePicture
                                  picture={company.logo_url}
                                  picturesmall={company.small_logo_url}
                                  size={36}
                                  initials={company.name?.[0] || "?"}
                                />
                                <Box>
                                  <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)", transition: "color 0.15s" }}>
                                    {app.job_offer?.job_title || app.job_title || "—"}
                                  </Typography>
                                  <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "12px", color: "hsl(178, 10%, 45%)" }}>
                                    {company.name || "—"}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{
                                display: "flex", alignItems: "center", gap: "6px",
                                px: "10px", py: "4px", borderRadius: "8px",
                                border: "1px solid hsl(160, 10%, 90%)", bgcolor: "hsla(160, 10%, 96%, 0.3)",
                              }}>
                                <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "11px", color: "hsl(178, 10%, 45%)" }}>
                                  {intl.formatMessage({ id: "candidate.dashboard.stage_label" })}
                                </Typography>
                                <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "11px", color: "hsl(178, 93%, 10%)" }}>
                                  {stages[current] || "—"}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Pipeline bars */}
                            <Box sx={{ display: "flex", width: "100%", gap: "4px", overflow: "hidden" }}>
                              {stages.map((name, i) => {
                                const stageId = `${app.id}-${i}`;
                                const isTapped = tappedStage === stageId;
                                return (
                                  <Tooltip key={i} title={`${name}${i === current ? " ●" : ""}`} placement="top" arrow>
                                    <Box
                                      onClick={(e) => { e.stopPropagation(); setTappedStage((prev) => (prev === stageId ? null : stageId)); }}
                                      sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px", cursor: "pointer" }}
                                    >
                                      <Typography sx={{
                                        fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        display: { xs: isTapped ? "block" : "none", sm: "block" },
                                        color: i === current ? "hsl(153, 68%, 25%)" : i < current ? "hsl(178, 10%, 45%)" : "hsla(178, 10%, 45%, 0.4)",
                                      }}>
                                        {name}
                                      </Typography>
                                      <Box sx={{
                                        height: "6px", width: "100%", borderRadius: "2px",
                                        transition: "all 0.2s",
                                        bgcolor: i < current ? "hsla(153, 68%, 25%, 0.6)" : i === current ? "hsla(153, 68%, 25%, 0.6)" : "hsl(160, 10%, 90%)",
                                        "&:hover": { transform: "scaleY(1.4)" },
                                      }} />
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        </motion.div>
                      );
                    })}
                    {pipelineApps.length === 0 && !loading && (
                      <EmptyState
                        card
                        icon={FileText}
                        title={intl.formatMessage({ id: "candidate.dashboard.no_active_applications" })}
                        subtitle={intl.formatMessage({ id: "candidate.dashboard.no_active_applications_hint" })}
                        ctaLabel={intl.formatMessage({ id: "candidate.dashboard.browse_offers" })}
                        onCta={() => onNavigate("jobs")}
                      />
                    )}
                  </Box>
                </Box>

                {/* Saved jobs */}
                <Box sx={{ mb: "2rem"}}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.saved_jobs" })}
                    </Typography>
                    <Box
                      component="button"
                      onClick={() => onNavigate("jobs", null, { onlyFavorites: true })}
                      sx={{
                        fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", display: "flex", alignItems: "center", gap: "4px",
                        border: "none", bgcolor: "transparent", cursor: "pointer", "&:hover": { opacity: 0.8 },
                      }}
                    >
                      {intl.formatMessage({ id: "candidate.dashboard.see_all" })} <ArrowRight size={12} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
                    {savedJobs.slice(0, 4).map((job) => {
                      const company = job.company || {};
                      const location = job.locations?.[0];
                      return (
                        <motion.div key={job.id} whileHover={{ y: -2 }}>
                          <Box
                            onClick={() => onNavigate("jobs", job)}
                            sx={{
                              bgcolor: "hsl(0, 0%, 100%)",
                              borderRadius: "16px",
                              border: "1px solid hsla(160, 10%, 90%, 0.6)",
                              p: "16px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: "hsla(145, 63%, 42%, 0.3)",
                                boxShadow: "0 10px 15px -3px rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <ProfilePicture
                                picture={company.logo_url}
                                picturesmall={company.small_logo_url}
                                size={36}
                                initials={company.name?.[0] || "?"}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  transition: "color 0.15s",
                                }}>
                                  {job.job_title}
                                </Typography>
                                <Typography sx={{ fontSize: "10px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)" }}>
                                  {company.name}{location ? ` · ${location.city}` : ""}
                                </Typography>
                              </Box>
                              <Box
                                component="button"
                                onClick={(e) => handleUnfavorite(e, job.id)}
                                aria-label={intl.formatMessage({ id: "candidate.dashboard.remove_favorite", defaultMessage: "Retirer des favoris" })}
                                sx={{
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  border: "none", bgcolor: "transparent", cursor: "pointer", p: "12px", m: "-6px",
                                  borderRadius: "6px", flexShrink: 0, transition: "all 0.15s",
                                  "&:hover": { bgcolor: "hsla(0, 0%, 0%, 0.05)" },
                                }}
                              >
                                <Bookmark size={14} fill="hsl(178, 93%, 10%)" color="hsl(178, 93%, 10%)" />
                              </Box>
                            </Box>
                          </Box>
                        </motion.div>
                      );
                    })}
                    {savedJobs.length === 0 && !loading && (
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <EmptyState
                          card
                          icon={Bookmark}
                          title={intl.formatMessage({ id: "candidate.dashboard.no_saved_jobs" })}
                          subtitle={intl.formatMessage({ id: "candidate.dashboard.no_saved_jobs_hint" })}
                          ctaLabel={intl.formatMessage({ id: "candidate.dashboard.browse_offers" })}
                          onCta={() => onNavigate("jobs")}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Right column */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* Profile completion */}
                <motion.div variants={item}>
                  <Box sx={{ bgcolor: "hsl(0, 0%, 100%)", borderRadius: "16px", border: "1px solid hsla(160, 10%, 90%, 0.6)", p: "20px" }}>
                    <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)", mb: "16px" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.profile_completion" })}
                    </Typography>

                    {completionPercent < 60 && (
                      <Box sx={{
                        display: "flex", alignItems: "flex-start", gap: "10px",
                        borderRadius: "12px", bgcolor: "hsla(0, 84.2%, 60.2%, 0.05)", border: "1px solid hsla(0, 84.2%, 60.2%, 0.1)",
                        px: "14px", py: "12px", mb: "16px",
                      }}>
                        <ShieldAlert size={14} color="hsl(0, 84.2%, 60.2%)" style={{ flexShrink: 0, marginTop: "2px" }} />
                        <Typography sx={{ fontSize: "11px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsla(0, 84.2%, 60.2%, 0.9)", lineHeight: 1.6 }}>
                          {intl.formatMessage({ id: "candidate.dashboard.visibility_warning_prefix" })}
                          <Typography component="span" sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "11px", color: "hsl(0, 84.2%, 60.2%)" }}>60%</Typography>
                          {intl.formatMessage({ id: "candidate.dashboard.visibility_warning_suffix" })}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      <ProfileGauge percent={completionPercent} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", lineHeight: 1.6, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                          {intl.formatMessage({ id: "candidate.dashboard.complete_profiles_prefix" })}
                          <Box component="span" sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>{intl.formatMessage({ id: "candidate.dashboard.complete_profiles_highlight" })}</Box>
                          {intl.formatMessage({ id: "candidate.dashboard.complete_profiles_suffix" })}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {visibleTips.map((tip) => {
                        const Icon = tip.icon;
                        return (
                          <Box
                            key={tip.id}
                            component="button"
                            onClick={() => onNavigate("profile", null, { profileSection: tip.section })}
                            sx={{
                              width: "100%", display: "flex", alignItems: "center", gap: "12px",
                              borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.4)", px: "14px", py: "10px",
                              transition: "all 0.15s", border: "none", cursor: "pointer", textAlign: "left",
                              "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.7)" },
                              "&:hover .tip-text": { color: "hsl(178, 93%, 10%)" },
                            }}
                          >
                            <Box sx={{
                              width: "28px", height: "28px", borderRadius: "8px",
                              bgcolor: "hsla(153, 68%, 25%, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                              <Icon size={13} color="hsl(153, 68%, 25%)" />
                            </Box>
                            <Box className="tip-text" component="span" sx={{
                              fontSize: "11px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", flex: 1, minWidth: 0,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              transition: "color 0.15s",
                            }}>
                              {intl.formatMessage({ id: tip.textId })}
                            </Box>
                            <Box component="span" sx={{
                              fontSize: "10px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)", flexShrink: 0,
                              bgcolor: "hsla(153, 68%, 25%, 0.1)", px: "8px", py: "2px", borderRadius: "9999px",
                            }}>
                            +{tip.weight}%
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {completionPercent < 100 && (
                      <Box
                        component="button"
                        onClick={() => onNavigate("profile")}
                        sx={{
                          mt: "16px", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px",
                          fontSize: "12px", color: "hsl(178, 93%, 10%)", border: "none", bgcolor: "transparent",
                          cursor: "pointer", transition: "opacity 0.15s", "&:hover": { opacity: 0.8 },
                        }}
                      >
                        {intl.formatMessage({ id: "candidate.dashboard.complete_my_profile" })}
                      </Box>
                    )}
                  </Box>
                </motion.div>

                {/* Who viewed your profile */}
                <motion.div variants={item}>
                  <Box sx={{ bgcolor: "hsl(0, 0%, 100%)", borderRadius: "16px", border: "1px solid hsla(160, 10%, 90%, 0.6)", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "20px", pt: "20px", pb: "12px" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                        {intl.formatMessage({ id: "candidate.dashboard.who_viewed_profile" })}
                      </Typography>
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: "6px",
                        px: "10px", py: "4px", borderRadius: "8px", bgcolor: "hsla(153, 68%, 25%, 0.1)",
                      }}>
                        <Eye size={12} color="hsl(153, 68%, 25%)" />
                        <Typography sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                          {profileViewsCount}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "hsla(153, 68%, 25%, 0.6)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                          {intl.formatMessage({ id: "candidate.dashboard.views" })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ px: "20px", pb: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      {profileViewers.map((v, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.08 }}
                        >
                          <Box
                            sx={{
                              display: "flex", alignItems: "center", gap: "12px",
                              p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)",
                              transition: "all 0.15s",
                            }}
                          >
                            {/* Blurred avatar */}
                            <Box sx={{
                              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                              bgcolor: "hsl(160, 10%, 88%)", filter: "blur(6px)",
                            }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Blurred company name */}
                              <Typography sx={{
                                fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)",
                                filter: "blur(5px)", userSelect: "none", display: "inline-block",
                              }}>
                                {intl.formatMessage({ id: "candidate.dashboard.confidential_company" })}
                              </Typography>
                              {v.company_industry && (
                                <Typography sx={{
                                  fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "capitalize",
                                }}>
                                  {v.company_industry}
                                </Typography>
                              )}
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "4px" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <Eye size={10} color="hsl(153, 68%, 25%)" />
                                  <Typography sx={{ fontSize: "10px", color: "hsl(153, 68%, 25%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                                    {intl.formatMessage({ id: "candidate.dashboard.viewed_your_profile" })}
                                  </Typography>
                                </Box>
                                {v.saved && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                    <Bookmark size={10} color="hsl(153, 68%, 25%)" fill="hsl(153, 68%, 25%)" />
                                    <Typography sx={{ fontSize: "10px", color: "hsl(153, 68%, 25%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                                      {intl.formatMessage({ id: "candidate.dashboard.saved_you" })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize: "11px", color: "hsla(178, 10%, 45%, 0.75)", flexShrink: 0, alignSelf: "flex-start", mt: "2px", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                              {v.viewed_at ? moment(v.viewed_at).fromNow() : ""}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))}
                      {profileViewers.length === 0 && !loading && (
                        <EmptyState
                          icon={Eye}
                          title={intl.formatMessage({ id: "candidate.dashboard.no_recent_views" })}
                          subtitle={intl.formatMessage({ id: "candidate.dashboard.no_recent_views_hint" })}
                          ctaLabel={intl.formatMessage({ id: "candidate.dashboard.complete_my_profile" })}
                          onCta={() => onNavigate("profile")}
                        />
                      )}
                    </Box>
                  </Box>
                </motion.div>

                {/* Matched companies */}
                <motion.div variants={item}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.recommended_companies" })}
                    </Typography>
                    <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: "11px", color: "hsl(178, 10%, 45%)" }}>
                      {intl.formatMessage({ id: "candidate.dashboard.companies_count" }, { count: matchedCompanies.length })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {matchedCompanies.map((c, i) => (
                      <CompanyCard
                        key={c.id || c.name}
                        company={c}
                        index={i}
                        enableAnimation={true}
                        onClick={() => {
                          window.location.href = `/${i18nLocale}/companies/${c.encoded_name}/${c.id}`;
                        }}
                        isFollowed={(currentCandidate?.followed_company_ids || []).map(String).includes(String(c.id))}
                        onFollowToggle={currentCandidate?.id ? handleFollowToggle : undefined}
                      />
                    ))}
                    {matchedCompanies.length === 0 && !loading && (
                      <EmptyState
                        card
                        icon={Target}
                        title={intl.formatMessage({ id: "candidate.dashboard.no_recommended_companies" })}
                        subtitle={intl.formatMessage({ id: "candidate.dashboard.no_recommended_companies_hint" })}
                        ctaLabel={intl.formatMessage({ id: "candidate.dashboard.discover_companies" })}
                        onCta={() => navigate(`/${i18nLocale}/companies`)}
                      />
                    )}
                  </Box>
                </motion.div>

                {/* Followed companies */}
                <motion.div variants={item}>
                  <Box sx={{ bgcolor: "hsl(0, 0%, 100%)", borderRadius: "16px", border: "1px solid hsla(160, 10%, 90%, 0.6)", p: "20px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "16px" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                        {intl.formatMessage({ id: "candidate.dashboard.followed_companies" })}
                      </Typography>
                      {followedCompanies.length > 0 && (
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: "6px",
                          px: "10px", py: "4px", borderRadius: "8px", bgcolor: "hsla(153, 68%, 25%, 0.1)",
                        }}>
                          <Heart size={12} color="hsl(153, 68%, 25%)" fill="hsl(153, 68%, 25%)" />
                          <Typography sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                            {followedCompanies.length}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {followedCompanies.length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {followedCompanies.map((c, i) => (
                          <motion.div
                            key={c.id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                          >
                            <Box
                              onClick={() => {
                                window.location.href = `/${i18nLocale}/companies/${c.encoded_name}/${c.id}`;
                              }}
                              sx={{
                                display: "flex", alignItems: "center", gap: "12px",
                                p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)",
                                cursor: "pointer", transition: "all 0.15s",
                                "&:hover": { bgcolor: "hsla(153, 68%, 25%, 0.05)", borderColor: "hsla(153, 68%, 25%, 0.2)" },
                              }}
                            >
                              <ProfilePicture
                                picture={c.logo_url}
                                picturesmall={c.small_logo_url}
                                size={36}
                                initials={c.name?.[0] || "?"}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                  {c.name}
                                </Typography>
                                {c.online_job_offers_count > 0 && (
                                  <Typography sx={{ fontSize: "10px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                                    {intl.formatMessage({ id: "candidate.dashboard.online_offers_count" }, { count: c.online_job_offers_count })}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                component="button"
                                onClick={(e) => handleUnfollow(e, c.id)}
                                aria-label={intl.formatMessage({ id: "candidate.dashboard.unfollow_company", defaultMessage: "Ne plus suivre" })}
                                sx={{
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  border: "none", bgcolor: "transparent", cursor: "pointer", p: "12px", m: "-6px",
                                  borderRadius: "6px", flexShrink: 0, transition: "all 0.15s",
                                  "&:hover": { bgcolor: "hsla(0, 0%, 0%, 0.05)" },
                                }}
                              >
                                <Heart size={14} fill="hsl(153, 68%, 25%)" color="hsl(153, 68%, 25%)" />
                              </Box>
                            </Box>
                          </motion.div>
                        ))}
                      </Box>
                    ) : (
                      !loading && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", py: "12px" }}>
                          <Box sx={{
                            width: "40px", height: "40px", borderRadius: "12px",
                            bgcolor: "hsla(153, 68%, 25%, 0.08)", display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <Heart size={18} color="hsl(178, 10%, 45%)" />
                          </Box>
                          <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", textAlign: "center", lineHeight: 1.6, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                            {intl.formatMessage({ id: "candidate.dashboard.no_followed_companies_prefix" })}
                            <Heart size={10} style={{ verticalAlign: "middle", display: "inline" }} color="hsl(153, 68%, 25%)" />
                            {intl.formatMessage({ id: "candidate.dashboard.no_followed_companies_suffix" })}
                          </Typography>
                          <Box
                            component="button"
                            onClick={() => navigate(`/${i18nLocale}/companies`)}
                            sx={{
                              mt: "4px", fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                              color: "hsl(153, 68%, 25%)", display: "flex", alignItems: "center", gap: "4px",
                              border: "none", bgcolor: "transparent", cursor: "pointer",
                              transition: "opacity 0.15s", "&:hover": { opacity: 0.8 },
                            }}
                          >
                            {intl.formatMessage({ id: "candidate.dashboard.discover_companies" })} <ArrowRight size={12} />
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>
    </motion.div>
  );
};

export default DashboardTab;
